import { capitalizeString } from '@order-management/format';
import { SalesOrderPermissions } from '@order-management/roles';
import {
  Account,
  InputType,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  SalesOrdersCriteria,
  SalesOrdersTableFilterDataType,
  SalesOrdersTableRow,
  SortDirection,
  User,
  UsersCriteria,
  UsersTableFilterDataType,
  UserStatus,
} from '@order-management/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { FaFileInvoice, FaUsers } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';
import SalesOrdersTable from '../../sales-orders/sales-orders-table/sales-orders-table';
import { salesOrderDetailsColumns } from '../../sales-orders/sales-orders-table/sales-orders-table-columns';
import BackButton from '../../shared/back-button/back-button';
import Conditional from '../../shared/conditional/conditional';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useHttpRequest from '../../shared/hooks/http-request';
import ProtectedPage from '../../shared/protected-page/protected-page';
import BoxLayout from '../../ui/box-layout/box-layout';
import DetailsCard from '../../ui/details-card/details-card';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import { OnTableChangeData } from '../../ui/table/types';
import ReadOnlyUsersTable from '../../users/read-only-users-table/read-only-users-table';
import { PermissionsGroupBySalesOrder } from '../../users/users-table/users-table';
import {
  fetchAccountById,
  fetchAccountRelatedSalesOrders,
  fetchAccountRelatedUsers,
} from '../accounts-service';

const defaultSalesOrdersTableCriteria: SalesOrdersCriteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: 'number',
  sortDirection: SortDirection.DES,
};

const defaultUsersTableCriteria: UsersCriteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: 'email',
  sortDirection: SortDirection.ASC,
};

export function AccountDetailsPage() {
  const params = useParams();
  const {
    executeHttpRequest: executeFetchAccount,
    isLoading: isLoadingAccount,
  } = useHttpRequest();
  const {
    executeHttpRequest: executeFetchSalesOrders,
    isLoading: isLoadingSalesOrders,
  } = useHttpRequest();
  const { executeHttpRequest: executeFetchUsers, isLoading: isLoadingUsers } =
    useHttpRequest();
  const [account, setAccount] = useState<Account>();
  const [relatedSalesOrders, setRelatedSalesOrders] = useState<
    PaginatedAPIResponse<SalesOrdersTableRow>
  >({
    data: [],
    count: 0,
    page: 0,
    total: 0,
    pages: 0,
  });
  const [relatedUsers, setRelatedUsers] = useState<PaginatedAPIResponse<User>>({
    data: [],
    count: 0,
    page: 0,
    total: 0,
    pages: 0,
  });
  const [salesOrdersTableCriteria, setSalesOrdersTableCriteria] =
    useState<SalesOrdersCriteria>(defaultSalesOrdersTableCriteria);
  const [usersTableCriteria, setUsersTableCriteria] = useState<UsersCriteria>(
    defaultUsersTableCriteria
  );

  const accountSalesOrders = useMemo(() => {
    const salesOrders = account?.opportunities?.reduce(
      (acc, current) => acc + current.salesOrders.length,
      0
    );
    const text = salesOrders === 1 ? 'sales order' : 'sales orders';
    return `${salesOrders} ${text}`;
  }, [account?.opportunities]);

  const salesOrdersTableConfig: PaginatedContentConfig = useMemo(
    () => ({
      ...salesOrdersTableCriteria,
      pages: relatedSalesOrders.pages,
    }),
    [relatedSalesOrders.pages, salesOrdersTableCriteria]
  );

  const usersTableConfig: PaginatedContentConfig = useMemo(
    () => ({
      ...usersTableCriteria,
      pages: relatedUsers.pages,
    }),
    [relatedUsers.pages, usersTableCriteria]
  );

  const emptyUsersContentText = useMemo(() => {
    const hasAppliedFilters = usersTableCriteria.search !== '';
    if (hasAppliedFilters) {
      return 'There are no users that match the selected criteria';
    }
    return 'There are no users at the moment';
  }, [usersTableCriteria.search]);

  const emptySalesOrdersContentText = useMemo(() => {
    const hasAppliedFilters = salesOrdersTableCriteria.search !== '';
    if (hasAppliedFilters) {
      return 'There are no sales orders that match the selected criteria';
    }
    return 'There are no sales orders at the moment';
  }, [salesOrdersTableCriteria.search]);

  const relatedSalesOrdersFiltersData = useMemo(() => {
    return {
      salesOrders: {
        title: 'Sales order',
        appliedCount: salesOrdersTableCriteria.salesOrders?.length,
        value: salesOrdersTableCriteria.salesOrders,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrders',
          valueKey: 'salesOrders',
          placeholder: 'Search by SO',
        },
      },
      endUser: {
        title: 'End user',
        appliedCount: salesOrdersTableCriteria.endUser?.length,
        value: salesOrdersTableCriteria.endUser,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'endUser',
          valueKey: 'endUser',
          placeholder: 'Search by End user',
        },
      },
    };
  }, [
    salesOrdersTableCriteria.endUser,
    salesOrdersTableCriteria.salesOrders,
  ]) as FiltersData<SalesOrdersCriteria>;

  const relatedUsersFiltersData = useMemo(() => {
    return {
      email: {
        title: 'User',
        appliedCount: usersTableCriteria.email?.length,
        value: usersTableCriteria.email,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'email',
          valueKey: 'email',
          placeholder: 'Search by User',
        },
      },
      salesOrder: {
        title: 'Sales order',
        appliedCount: usersTableCriteria.salesOrder?.length,
        value: usersTableCriteria.salesOrder,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrder',
          valueKey: 'salesOrder',
          placeholder: 'Search by SO',
        },
      },
      status: {
        title: 'Status',
        appliedCount: usersTableCriteria.status?.length,
        value: usersTableCriteria.status,
        input: {
          type: InputType.SELECT,
          valueKey: 'status',
          options: [UserStatus.ACTIVE, UserStatus.PENDING_FIRST_LOGIN].map(
            (status) => ({
              label: capitalizeString(status),
              value: status,
            })
          ),
        },
      },
    };
  }, [
    usersTableCriteria.email,
    usersTableCriteria.salesOrder,
    usersTableCriteria.status,
  ]) as FiltersData<UsersCriteria>;

  const fetchAccountDetails = useCallback(
    async () =>
      executeFetchAccount({
        asyncFunction: async () => {
          const accountId = params.id;
          const account = await fetchAccountById(accountId!);
          setAccount(account);
        },
      }),
    [executeFetchAccount, params.id]
  );

  const fetchRelatedSalesOrders = useCallback(
    () =>
      executeFetchSalesOrders({
        asyncFunction: async () => {
          const salesOrders = await fetchAccountRelatedSalesOrders(
            params.id!,
            salesOrdersTableCriteria
          );
          setRelatedSalesOrders(salesOrders);
        },
      }),
    [executeFetchSalesOrders, params.id, salesOrdersTableCriteria]
  );

  const fetchRelatedUsers = useCallback(
    async () =>
      executeFetchUsers({
        asyncFunction: async () => {
          const users = await fetchAccountRelatedUsers(
            params.id!,
            usersTableCriteria
          );
          setRelatedUsers(users);
        },
      }),
    [executeFetchUsers, params.id, usersTableCriteria]
  );

  const handleOnSalesOrdersTableChange = useCallback(
    (value: OnTableChangeData<SalesOrdersTableRow>) => {
      const newData = {
        ...salesOrdersTableCriteria,
        ...value.config,
        sortBy: value.column?.key || salesOrdersTableCriteria.sortBy,
      };
      setSalesOrdersTableCriteria(newData);
    },
    [salesOrdersTableCriteria]
  );

  const handleOnUsersTableChange = useCallback(
    (value: OnTableChangeData<User, PermissionsGroupBySalesOrder>) => {
      const newData = {
        ...usersTableCriteria,
        ...value.config,
        sortBy: value.column?.key || usersTableCriteria.sortBy,
      };
      setUsersTableCriteria(newData);
    },
    [usersTableCriteria]
  );

  const handleSalesOrdersTableFilterSubmit = useCallback(
    (tableFilterValue: SalesOrdersTableFilterDataType) => {
      const newCriteria: SalesOrdersCriteria = {
        ...salesOrdersTableCriteria,
        ...tableFilterValue,
        pageNumber: 1,
      };
      setSalesOrdersTableCriteria(newCriteria);
    },
    [salesOrdersTableCriteria]
  );

  const onRemoveRelatedSalesOrdersFilters = useCallback(
    (filterDataKeys: (keyof SalesOrdersCriteria)[]) => {
      const newData: SalesOrdersCriteria = {
        ...salesOrdersTableCriteria,
        pageNumber: 1,
      };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue =
          defaultSalesOrdersTableCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            number;
        }
      }
      setSalesOrdersTableCriteria(newData);
    },
    [salesOrdersTableCriteria]
  );

  const handleUsersTableFilterSubmit = useCallback(
    (tableFilterValue: UsersTableFilterDataType) => {
      const newUsersTableCriteria = {
        ...usersTableCriteria,
        ...tableFilterValue,
        pageNumber: 1,
      };
      setUsersTableCriteria(newUsersTableCriteria);
    },
    [usersTableCriteria]
  );

  const onRemoveRelatedUsersFilters = useCallback(
    (filterDataKeys: (keyof UsersCriteria)[]) => {
      const newUsersTableCriteria = { ...usersTableCriteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = defaultUsersTableCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newUsersTableCriteria[filterDataKey];
        } else {
          newUsersTableCriteria[filterDataKey] = initialFilterValue as string &
            string[] &
            boolean;
        }
      }
      setUsersTableCriteria(newUsersTableCriteria);
    },
    [usersTableCriteria]
  );

  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  useEffect(() => {
    fetchRelatedSalesOrders();
  }, [fetchRelatedSalesOrders, salesOrdersTableCriteria]);

  useEffect(() => {
    fetchRelatedUsers();
  }, [fetchRelatedUsers, usersTableCriteria]);

  return (
    <ProtectedPage
      permissions={
        SalesOrderPermissions.SALES_ORDERS_ACCOUNT_AND_PARENT_ACCOUNT_READ
      }
    >
      <Conditional condition={isLoadingAccount || !account}>
        <Conditional.True>
          <PageSpinner />
        </Conditional.True>
        <Conditional.False>
          <BoxLayout
            className="is-min-height-80 mx-10 my-4"
            header={
              <Element className="p-4">
                <BackButton />
                <h1 className="is-size-3 has-text-weight-light">
                  {account?.name}
                </h1>
                <h2 className="is-size-5 has-text-weight-light">
                  {accountSalesOrders}
                </h2>
              </Element>
            }
          >
            <DetailsCard>
              <Element pb={6}>
                <Icon color="primary" mr={2}>
                  <FaFileInvoice size={24} />
                </Icon>
                <span className="is-size-4 has-text-weight-light">
                  Related sales orders
                </span>
              </Element>
              <Conditional condition={isLoadingSalesOrders}>
                <Conditional.True>
                  <PageSpinner />
                </Conditional.True>
                <Conditional.False>
                  <Element display="flex" flexDirection="column">
                    <Element
                      display="flex"
                      className="w-full pb-2"
                      alignItems="center"
                    >
                      <FilterBar
                        initialData={defaultSalesOrdersTableCriteria}
                        data={salesOrdersTableCriteria}
                        filtersData={relatedSalesOrdersFiltersData}
                        onFilterSubmit={handleSalesOrdersTableFilterSubmit}
                        onRemoveFilter={onRemoveRelatedSalesOrdersFilters}
                      />
                    </Element>
                    <SalesOrdersTable
                      columns={salesOrderDetailsColumns}
                      dataSource={relatedSalesOrders.data}
                      config={salesOrdersTableConfig}
                      onChange={handleOnSalesOrdersTableChange}
                      empty={
                        <Element
                          display="flex"
                          justifyContent="center"
                          className="my-2 has-text-weight-light"
                        >
                          <span>{emptySalesOrdersContentText}</span>
                        </Element>
                      }
                      form={{ enableSearch: false }}
                    />
                  </Element>
                </Conditional.False>
              </Conditional>
            </DetailsCard>
            <DetailsCard>
              <Element pb={6}>
                <Icon color="primary" mr={2}>
                  <FaUsers size={24} />
                </Icon>
                <span className="is-size-4 has-text-weight-light">
                  Related users
                </span>
              </Element>
              <Conditional condition={isLoadingUsers}>
                <Conditional.True>
                  <PageSpinner />
                </Conditional.True>
                <Conditional.False>
                  <Element display="flex" flexDirection="column">
                    <Element
                      display="flex"
                      className="w-full pb-2"
                      alignItems="center"
                    >
                      <FilterBar
                        initialData={defaultUsersTableCriteria}
                        data={usersTableCriteria}
                        filtersData={relatedUsersFiltersData}
                        onFilterSubmit={handleUsersTableFilterSubmit}
                        onRemoveFilter={onRemoveRelatedUsersFilters}
                      />
                    </Element>
                    <ReadOnlyUsersTable
                      dataSource={relatedUsers.data}
                      config={usersTableConfig}
                      onChange={handleOnUsersTableChange}
                      relatedAccountId={params.id!}
                      empty={
                        <Element
                          display="flex"
                          justifyContent="center"
                          className="my-2"
                        >
                          <span className="has-text-weight-light">
                            {emptyUsersContentText}
                          </span>
                        </Element>
                      }
                      form={{
                        enableSearch: false,
                      }}
                    />
                  </Element>
                </Conditional.False>
              </Conditional>
            </DetailsCard>
          </BoxLayout>
        </Conditional.False>
      </Conditional>
    </ProtectedPage>
  );
}

export default AccountDetailsPage;
